import { type BusinessLocationType } from '@/utils/constants/storeLocations/businessLocationType'
import { type AddressProps } from '../mockAddress'
import { type StoreLocationCategoryType } from '../mockStoreLocationCategories'
import { type HolidayHoursProps } from '../storeLocationHours/mockHolidayHours'
import { type RegularHourListProps } from '../storeLocationHours/mockRegularHours'
import { type TemporarilyClosedHourProps } from '../storeLocationHours/mockTemporarilyClosedHours'
import { type BusinessOwnershipProps } from './mockBusinessOwnership'

export enum ATTIRE_VALUES {
  casual = 'Casual',
  dressy = 'Dressy',
  formal = 'Formal',
  unspecified = 'UNSPECIFIED',
}

export enum SERVICE_OFFERINGS_VALUES {
  dineIn = 'Dine_In',
  waiterService = 'Table_Service',
  counterService = 'Counter_Service',
  catering = 'Catering',
}

export enum TAKEOUT_AND_DELIVERY_SERVICES_VALUES {
  takeout = 'In_Store',
  curbSide = 'Curbside_Pickup',
  delivery = 'Delivery',
  driveThrough = 'Drive_Thru',
  orderAndPayAhead = 'Order_And_Pay_Ahead',
}

export type TakeoutAndDeliveryServiceType =
  | SERVICE_OFFERINGS_VALUES
  | TAKEOUT_AND_DELIVERY_SERVICES_VALUES

export enum AMENITIES_VALUES {
  barOnsite = 'barOnsite',
  coatCheck = 'coatCheck',
  fireplace = 'fireplace',
  genderNeutralRestroom = 'genderNeutralRestroom',
  highChairs = 'highChairs',
  niceView = 'niceView',
  outdoorSeating = 'outdoorSeating',
  outdoorHeating = 'outdoorHeating',
  restroom = 'restroom',
  rooftopSeating = 'rooftopSeating',
  seating = 'seating',
  waterfront = 'waterfront',
  changingTable = 'changingTable',
}

export enum ENTERTAINMENT_VALUES {
  liveMusic = 'liveMusic',
  suitableForWatchingSports = 'suitableForWatchingSports',
  tv = 'tv',
}

export enum FRIENDLY_SPACE_DETAILS_VALUES {
  dogFriendly = 'dogFriendly',
  familyFriendly = 'familyFriendly',
  goodForKids = 'goodForKids',
  LGBTQFriendly = 'LGBTQFriendly',
  transgenderSafespace = 'transgenderSafespace',
}

export enum WIFI_DETAILS_VALUES {
  free = 'free',
  paid = 'paid',
  none = 'none',
  unspecified = 'unspecified',
}

export enum PARKING_VALUES {
  parkingLot = 'parkingLot',
  streetParking = 'streetParking',
  valetParking = 'valetParking',
  validatedParking = 'validatedParking',
  hasParkingLotFree = 'hasParkingLotFree',
  hasParkingLotPaid = 'hasParkingLotPaid',
  hasParkingStreetFree = 'hasParkingStreetFree',
  hasParkingStreetPaid = 'hasParkingStreetPaid',
  hasParkingGarageFree = 'hasParkingGarageFree',
  hasParkingGaragePaid = 'hasParkingGaragePaid',
  hasOnsiteParking = 'hasOnsiteParking',
}

export enum ACCESSIBILITY_VALUES {
  brailleMenu = 'brailleMenu',
  hearingLoop = 'hearingLoop',
  wheelchairAccessibleElevator = 'wheelchairAccessibleElevator',
  wheelchairAccessibleEntrance = 'wheelchairAccessibleEntrance',
  wheelchairAccessibleParking = 'wheelchairAccessibleParking',
  wheelchairAccessibleRestroom = 'wheelchairAccessibleRestroom',
  wheelchairAccessibleSeating = 'wheelchairAccessibleSeating',
}

export enum SMOKING_DETAILS_OPTIONS_VALUES {
  yes = 'yes',
  no = 'no',
  outdoors = 'outdoors',
  unspecified = 'unspecified',
}

export enum DINING_OPTIONS_VALUES {
  breakfast = 'Breakfast',
  brunch = 'Brunch',
  lunch = 'Lunch',
  dinner = 'Dinner',
  happyHourFood = 'Happy_Hour_Food',
  happyHourDrinks = 'Happy_Hour_Drinks',
  lateNight = 'Late_Night',
}

export enum SPECIALTY_OFFERINGS_VALUES {
  childrenMenu = 'Children_Menu',
  comfortFood = 'comfortFood',
  dessert = 'Dessert',
  halalFood = 'halalFood',
  kosher = 'Kosher',
  localSpecialty = 'localSpecialty',
  organic = 'Organic',
  saladBar = 'Salad_Bar',
  smallPlates = 'Small_Plates',
  vegan = 'Vegan',
  vegetarian = 'Vegetarian',
}

export enum PAYMENT_OPTIONS_VALUES {
  americanExpress = 'American_Express',
  applePay = 'Apple_Pay',
  cash = 'Cash',
  check = 'Check',
  chinaUnionPay = 'China_Union_Pay',
  debitCards = 'Debit_Cards',
  dinersClub = 'Diners_Club',
  discover = 'Discover',
  googlePay = 'Google_Pay',
  japaneseCreditBureau = 'Japanese_Credit_Bureau',
  mastercard = 'MasterCard',
  samsungPay = 'Samsung_Pay',
  visa = 'Visa',
  creditCards = 'Credit_Cards',
}

export enum PRICE_RANGE_VALUES {
  one = 'ONE_DOLLAR',
  two = 'TWO_DOLLARS',
  three = 'THREE_DOLLARS',
  four = 'FOUR_DOLLARS',
  unspecified = 'UNSPECIFIED',
}

export enum ALCOHOL_SERVED_VALUES {
  beer = 'beer',
  wine = 'wine',
  hardLiquor = 'hardLiquor',
  cocktails = 'cocktails',
}

export enum WHEELCHAIR_RENTAL_OFFERINGS_VALUES {
  motorizedWheelchairs = 'motorized_wheelchairs',
  nonMotorizedWheelchairs = 'non_motorized_wheelchairs',
}

export type AccessibilityDetails = {
  hasAssistedListeningDevices?: boolean | null
  hasAuracastBroadcastAudio?: boolean | null
  hasHearingLoop?: boolean | null
  hasWheelchairAccessibleEntrance?: boolean | null
  hasWheelchairAccessibleParking?: boolean | null
  hasWheelchairAccessibleRestroom?: boolean | null
  hasWheelchairAccessibleSeating?: boolean | null
  wheelchairRentalOfferings?: WHEELCHAIR_RENTAL_OFFERINGS_VALUES[] | null
}

export type OfferingDetails = {
  allowsCampingVehiclesRecreational?: boolean | null
  allowsGrilling?: boolean | null
  buysGoodsUsed?: boolean | null
  hasAllYouCanEatAlways?: boolean | null
  hasArcadeGames?: boolean | null
  hasBakery?: boolean | null
  hasBrailleMenu?: boolean | null
  hasCarRental?: boolean | null
  hasCarWash?: boolean | null
  hasCashAdvance?: boolean | null
  hasCheckCashing?: boolean | null
  hasDancing?: boolean | null
  hasFreeAir?: boolean | null
  hasFullServiceGas?: boolean | null
  hasGiftShop?: boolean | null
  hasOilChange?: boolean | null
  hasOnsitePassportPhotos?: boolean | null
  hasPharmacy?: boolean | null
  hasPrivateDiningRoom?: boolean | null
  hasRvElectricHookup?: boolean | null
  hasRvSewageHookup?: boolean | null
  hasRvWaterHookup?: boolean | null
  hasSaladBar?: boolean | null
  hasServiceAssembly?: boolean | null
  hasServiceGuarantee?: boolean | null
  hasServiceRepair?: boolean | null
  offersBeverage?: boolean | null
  offersGolfCarts?: boolean | null
  sellsClothingForMen?: boolean | null
  sellsClothingForWomen?: boolean | null
  sellsCutFlowers?: boolean | null
  sellsFoodPrepared?: boolean | null
  sellsGasDiesel?: boolean | null
  sellsGasEthanolFree?: boolean | null
  sellsGoodsUsed?: boolean | null
  sellsOrganicProducts?: boolean | null
  sellsShoesForChildren?: boolean | null
  sellsShoesForMen?: boolean | null
  sellsShoesForWomen?: boolean | null
  sellsToysForChildren?: boolean | null
  servesAlcohol?: boolean | null
  servesBeer?: boolean | null
  servesCocktails?: boolean | null
  servesComfortFood?: boolean | null
  servesFood?: boolean | null
  servesFoodAtBar?: boolean | null
  servesHalalFood?: boolean | null
  servesHappyHourDrinks?: boolean | null
  servesHappyHourFood?: boolean | null
  servesLateNightFood?: boolean | null
  servesLiquor?: boolean | null
  servesOrganic?: boolean | null
  servesSmallPlates?: boolean | null
  servesVegan?: boolean | null
  servesVegetarian?: boolean | null
  servesWine?: boolean | null
}

export enum LANGUAGES_SPOKEN_VALUES {
  americanSignLanguageUsed = 'american_sign_language_used',
  arabicSpoken = 'arabic_spoken',
  cantoneseSpoken = 'cantonese_spoken',
  englishSpoken = 'english_spoken',
  filipinoSpoken = 'filipino_spoken',
  frenchSpoken = 'french_spoken',
  germanSpoken = 'german_spoken',
  haitianCreoleSpoken = 'haitian_creole_spoken',
  hindiSpoken = 'hindi_spoken',
  italianSpoken = 'italian_spoken',
  koreanSpoken = 'korean_spoken',
  mandarinSpoken = 'mandarin_spoken',
  polishSpoken = 'polish_spoken',
  portugueseSpoken = 'portuguese_spoken',
  romanianSpoken = 'romanian_spoken',
  russianSpoken = 'russian_spoken',
  spanishSpoken = 'spanish_spoken',
  ukrainianSpoken = 'ukrainian_spoken',
  vietnameseSpoken = 'vietnamese_spoken',
}

export type ServiceOptionDetails = {
  hasCurbsidePickup?: boolean | null
  hasDelivery?: boolean | null
  hasDeliverySameDay?: boolean | null
  hasDriveThrough?: boolean | null
  hasGroceryPickup?: boolean | null
  hasInStorePickup?: boolean | null
  hasInStoreShopping?: boolean | null
  hasMealService?: boolean | null
  hasNoContactDelivery?: boolean | null
  hasOnsiteServices?: boolean | null
  hasOutdoorServices?: boolean | null
  hasSeatingOutdoors?: boolean | null
  hasTakeout?: boolean | null
  offersOnlineAppointments?: boolean | null
  offersOnlineClasses?: boolean | null
  offersOnlineEstimates?: boolean | null
  servesDineIn?: boolean | null
  languagesSpoken?: LANGUAGES_SPOKEN_VALUES[] | null
}

export enum SWIMMING_POOL_OFFERINGS_VALUES {
  indoorPool = 'indoor_pool',
  outdoorPool = 'outdoor_pool',
}

export type AmenityDetails = {
  hasBaggageStorage?: boolean | null
  hasBarOnsite?: boolean | null
  hasBarbecueGrill?: boolean | null
  hasBaseballField?: boolean | null
  hasBasketballCourt?: boolean | null
  hasBusinessCenter?: boolean | null
  hasCafe?: boolean | null
  hasChildCare?: boolean | null
  hasConcierge?: boolean | null
  hasCyclingLanes?: boolean | null
  hasElevator?: boolean | null
  hasFitnessCenter?: boolean | null
  hasGolfCourse?: boolean | null
  hasGolfSimulator?: boolean | null
  hasHotTub?: boolean | null
  hasInUnitWasherDryer?: boolean | null
  hasMechanic?: boolean | null
  hasOnsiteConvenienceStore?: boolean | null
  hasOnsiteMaintenance?: boolean | null
  hasPettingZoo?: boolean | null
  hasPicnicTables?: boolean | null
  hasPlaygroundSlides?: boolean | null
  hasPlaygroundSwings?: boolean | null
  hasPrivateOutdoorSpace?: boolean | null
  hasRestaurant?: boolean | null
  hasRestroom?: boolean | null
  hasRestroomPublic?: boolean | null
  hasRestroomUnisex?: boolean | null
  hasRunningWater?: boolean | null
  hasSauna?: boolean | null
  hasShowersPublic?: boolean | null
  hasSkateboardArea?: boolean | null
  hasStadiumSeating?: boolean | null
  hasSwimmingPool?: boolean | null
  hasTennisCourts?: boolean | null
  hasTentSite?: boolean | null
  hasVolleyballCourt?: boolean | null
  offersTours?: boolean | null
  welcomesPets?: boolean | null
  hasShower?: boolean | null
  suitableForWorkingOnLaptop?: boolean | null
  swimmingPoolOfferings?: SWIMMING_POOL_OFFERINGS_VALUES[] | null
  hasCarVacuum?: boolean | null
}

export type ChildrenDetails = {
  hasAreaPlay?: boolean | null
  hasChangingTables?: boolean | null
  hasChildrensMenu?: boolean | null
  hasDiscountsForFamilies?: boolean | null
  hasDiscountsForKids?: boolean | null
  hasHighChairs?: boolean | null
  hasKidFriendlyActivities?: boolean | null
  hasLactationSpace?: boolean | null
  welcomesChildren?: boolean | null
}

export type ParkingDetails = {
  hasOnsiteParking?: boolean | null
  hasParkingGarageFree?: boolean | null
  hasParkingGaragePaid?: boolean | null
  hasParkingLotFree?: boolean | null
  hasParkingLotPaid?: boolean | null
  hasParkingStreetFree?: boolean | null
  hasParkingStreetPaid?: boolean | null
  hasParkingValet?: boolean | null
}

export type PetsDetails = {
  allowsDogsInside?: boolean | null
  allowsDogsOutside?: boolean | null
  hasDogPark?: boolean | null
  welcomesDogs?: boolean | null
}

export type CrowdDetails = {
  isTransgenderSafespace?: boolean | null
  welcomesFamilies?: boolean | null
  welcomesLgbtq?: boolean | null
}

export type ActivityDetails = {
  acceptsNewVolunteers?: boolean | null
  canPlayEighteenHoleCourse?: boolean | null
  canPlayNineHoleCourse?: boolean | null
  hasBicyclesForRent?: boolean | null
  hasDrivingRange?: boolean | null
  hasHiking?: boolean | null
  hasTouchTank?: boolean | null
}

export type HighlightDetails = {
  hasBarGames?: boolean | null
  hasDiscountsForActiveMilitary?: boolean | null
  hasFireplace?: boolean | null
  hasKaraokeNights?: boolean | null
  hasLiveMusic?: boolean | null
  hasLivePerformances?: boolean | null
  hasMovies3D?: boolean | null
  hasSeatingRooftop?: boolean | null
  hasTriviaNight?: boolean | null
  localSpecialty?: boolean | null
  suitableForWatchingSports?: boolean | null
}

export type PaymentDetails = {
  hasCampingFee?: boolean | null
  payCheck?: boolean | null
  payCreditCard?: boolean | null
  payDebitCard?: boolean | null
  payMobileNfc?: boolean | null
  payMobileTez?: boolean | null
  requiresCashOnly?: boolean | null
}

export type DiningOptionDetails = {
  hasSeating?: boolean | null
  hasCounterService?: boolean | null
  allowsOutsideFood?: boolean | null
  hasCatering?: boolean | null
  hasOrderAndPayAhead?: boolean | null
  servesBreakfast?: boolean | null
  servesBrunch?: boolean | null
  servesDessert?: boolean | null
  servesDinner?: boolean | null
  servesLunch?: boolean | null
}

export type PlanningDetails = {
  requiresReservations?: boolean | null
  requiresAppointments?: boolean | null
  acceptsWalkIns?: boolean | null
  requiresMembership?: boolean | null
  requiresEligibilityVerification?: boolean | null
  hasDressCode?: boolean | null
}

export type RecyclingDetails = {
  hasRecyclingElectronics?: boolean | null
  hasRecyclingClothing?: boolean | null
  hasRecyclingBatteries?: boolean | null
  hasRecyclingLightBulbs?: boolean | null
  hasRecyclingMetalCans?: boolean | null
  hasRecyclingInkCartridges?: boolean | null
  hasRecyclingGlassBottles?: boolean | null
  hasRecyclingPlasticBottles?: boolean | null
  hasRecyclingPlasticBags?: boolean | null
  hasRecyclingHouseholdHazardousWaste?: boolean | null
  hasRecyclingPlasticFoam?: boolean | null
}

export interface BusinessDetailsProps extends BusinessOwnershipProps {
  description?: string
  openingDate?: string | null
  attire?: ATTIRE_VALUES
  paymentOptions?: PAYMENT_OPTIONS_VALUES[]
  priceRange?: PRICE_RANGE_VALUES
  pickupAndDeliveryServices?: TakeoutAndDeliveryServiceType[]
  meals?: DINING_OPTIONS_VALUES[]
  specialtyFood?: SPECIALTY_OFFERINGS_VALUES[]
  alwaysAllYouCanEat?: boolean | null
  servesAlcohol?: boolean | null
  alcoholServed?: ALCOHOL_SERVED_VALUES[]
  servesCoffee?: boolean | null
  acceptsReservations?: boolean | null
  proofOfVacinationRequired?: boolean | null
  fullyVaccinatedStaff?: boolean | null
  amenities?: AMENITIES_VALUES[]
  entertainment?: ENTERTAINMENT_VALUES[]
  friendlySpaceDetails?: FRIENDLY_SPACE_DETAILS_VALUES[]
  wifiDetails?: WIFI_DETAILS_VALUES
  parking?: PARKING_VALUES[]
  accessibility?: ACCESSIBILITY_VALUES[]
  smokingDetails?: SMOKING_DETAILS_OPTIONS_VALUES
  yearEstablished?: number | null
  businessOwnerFirstName?: string | null
  businessOwnerFirstLetterInLastName?: string | null
  businessOwnerBiography?: string | null
  businessHistory?: string | null

  // Omni fields
  accessibilityDetails?: AccessibilityDetails
  offeringDetails?: OfferingDetails
  serviceOptionDetails?: ServiceOptionDetails
  amenityDetails?: AmenityDetails
  childrenDetails?: ChildrenDetails
  parkingDetails?: ParkingDetails
  petsDetails?: PetsDetails
  crowdDetails?: CrowdDetails
  activityDetails?: ActivityDetails
  highlightDetails?: HighlightDetails
  paymentDetails?: PaymentDetails
  diningOptionDetails?: DiningOptionDetails
  planningDetails?: PlanningDetails
  recyclingDetails?: RecyclingDetails
}

export interface BusinessLocationProps {
  lat: number
  lng: number
  locationType: BusinessLocationType
}

export interface ContactDetailsProps {
  alternatePhoneCountryCode?: string
  alternatePhoneNumber?: string
  androidAppUrl?: string
  iosAppUrl?: string
  reservationLinkUrl?: string | null
  websiteLinkUrl?: string | null
  menuLinkUrl?: string | null
  orderLinkUrl?: string | null
  textMessagingPhoneCountryCode?: string
  textMessagingPhoneNumber?: string
  whatsappPhoneCountryCode?: string
  whatsappPhoneNumber?: string

  // Omni fields
  urlAppointment?: string | null
  urlCovid19InfoPage?: string | null
  urlInventorySearch?: string | null
  urlShopOnline?: string | null
}

export interface ServiceAreaProps {
  placeName: string
  placeId: string
}

export interface CoreInformationProps extends AddressProps {
  title: string
  categories: StoreLocationCategoryType[]
  primaryPhoneCountryCode?: string
  primaryPhoneNumber?: string
  hideAddressInListing: boolean
  languageCode: string
  serviceAreas: ServiceAreaProps[]
  storeCode: string
}

export interface PhotoGalleryItemProps {
  altText?: string
  externalMediaId: string | null
  description?: string
  mediaId: string
  fileUrl: string
  id: string
}

export interface MediaProps {
  logoAltText?: string
  logoDescription?: string
  logoUrl?: string
  logoExternalId?: string
  photoGallery?: PhotoGalleryItemProps[]
}

export interface BrandedLogoProps {
  fileUrl?: string | null
  externalMediaId?: string
  altText?: string
  description?: string
}

export interface SocialAccountsProps {
  instagramHandle?: string | null
  linkedinUrl?: string | null
  twitterHandle?: string | null
}

export interface WebsitesAndSocialMediaProps {
  businessEmail?: string
  googleCoverAltText?: string
  googleCoverDescription?: string
  googleCoverUrl?: string | null
  googleLogoAltText?: string
  googleLogoDescription?: string
  googleLogoUrl?: string | null
  youtubeUrl?: string | null
  pinterestUrl?: string | null
  wikipediaUrl?: string
  tumblrUrl?: string
  facebookUrl?: string | null
  tikTokUrl?: string | null
}

export type StoreLocationDetailsType = CoreInformationProps & {
  businessDetails?: BusinessDetailsProps
  businessLocations?: BusinessLocationProps[]
  contactDetails?: ContactDetailsProps
  exceptionalHoursSet: HolidayHoursProps[]
  id: string
  media?: MediaProps
  brandedLogo?: BrandedLogoProps
  regularHoursSet: RegularHourListProps[]
  socialAccounts?: SocialAccountsProps
  temporaryClosureHours: TemporarilyClosedHourProps[]
  websitesAndSocialMedia?: WebsitesAndSocialMediaProps
  isPermanentlyClosed: boolean
  isOmniLocation?: boolean | null
}
